import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Avatar,
  Badge,
  Spinner,
  Divider,
  IconButton,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
  Flex,
  Image,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  useBreakpointValue,
} from '@chakra-ui/react'
import {
  X,
  Buildings,
  Plug,
  EnvelopeSimple,
  Phone,
  Calendar,
  ClockCounterClockwise,
  ChatDots,
  Eye,
  Download,
  ShoppingBag,
  UserCircle,
  Ticket,
  Plus,
} from '@phosphor-icons/react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import CreateTicketModal from '../InternalTickets/CreateTicketModal'
import AccessTags, { type AccessTag } from '../../components/AccessTags'
import GuestInfoPanel from './GuestInfoPanel'

interface UserInfo {
  user: {
    id: number
    name: string
    email: string
    phone: string | null
    profilePic: string | null
    createdAt: string
  }
  /** Todas as flags ligadas em "Planos e Acessos" do admin. */
  accessTags: AccessTag[]
  /** Integrações contratadas do AutoPilot (só quando a flag está ligada). */
  webotIntegrationsLimit: number | null
  corporates: {
    total: number
    list: {
      id: number
      corporateName: string
      employerNumber: string
      status: string
      walletBalance: number
      stateRegistration: string | null
      cityRegistration: string | null
      zipCode: string
      address: string
      number: string | null
      complement: string | null
      neighborhood: string
      city: string
      state: string
      mode: string | null
      taxRegime: number | null
      specialTaxRegime: number | null
      createdAt: string
      updatedAt: string
      razaoSocial: string | null
      nomeFantasia: string | null
      ie: string | null
      ieSt: string | null
      im: string | null
    }[]
  }
  integrations: {
    total: number
    list: {
      id: number
      name: string
      status: string
      channel: { id: number; name: string; term: string }
      corporate: { id: number; corporateName: string } | null
      itemsCount: number
    }[]
  }
  orders: {
    total: number
  }
  wallet: {
    totalBalance: number
    balancesByCorporate: {
      corporateId: number
      corporateName: string
      balance: number
    }[]
  }
}

interface ChatsCount {
  pending: number
  inProgress: number
  completed: number
  total: number
}

interface CompletedChat {
  id: number
  protocol: string | null
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
  categoryName: string | null
  categoryColor: string | null
  messageCount: number
  agentId: number | null
  agentName: string | null
  agentProfilePic: string | null
}

interface ChatMessage {
  id: number
  message: string
  senderType: string
  senderId: number | null
  messageType: string
  fileUrl: string | null
  fileName: string | null
  fileSize: number | null
  createdAt: string
  senderName: string | null
  senderProfilePic: string | null
}

interface InternalTicketSummary {
  id: number
  subject: string
  status: string
  priority: string
  createdAt: string
  creatorName: string
  assignedName: string
  orderId?: number | null
  supportChatId?: number | null
}

interface OrderTicketSummary {
  id: number
  orderId: number | null
  status: string
  createdAt: string
  lastInteration: string | null
  resolvedAt: string | null
  categoryName: string | null
  customStatusName: string | null
  customStatusColor: string | null
  channelOrderNumber: string | null
}

interface TicketMessage {
  id: number
  message: string | null
  messageType: string
  senderType?: string
  statusChange?: string | null
  fileUrl: string | null
  fileName: string | null
  fileSize: number | null
  caption?: string | null
  createdAt: string
  senderName: string | null
  senderProfilePic: string | null
}

const orderTicketStatusLabels: Record<string, string> = {
  pending: 'Pendente',
  answer_pending: 'Aguardando Resposta',
  closed: 'Fechado',
}

const orderTicketStatusColors: Record<string, string> = {
  pending: 'yellow',
  answer_pending: 'orange',
  closed: 'gray',
}

const ticketPriorityColors: Record<string, string> = {
  low: 'gray',
  medium: 'blue',
  high: 'orange',
  urgent: 'red',
}

const ticketPriorityLabels: Record<string, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
}

const ticketStatusLabels: Record<string, string> = {
  open: 'Aberto',
  in_progress: 'Em Andamento',
  waiting_response: 'Aguardando',
  resolved: 'Concluído',
}

const ticketStatusColors: Record<string, string> = {
  open: 'yellow',
  in_progress: 'blue',
  waiting_response: 'orange',
  resolved: 'green',
}

export default function UserInfoSidebar({
  chatId: _chatId,
  userId,
  onClose,
  isOpen,
  isGuest = false,
  guestLinkedUserId = null,
  onGuestLinked,
  embedded = false,
}: {
  chatId: number
  userId: number
  onClose: () => void
  isOpen?: boolean
  /** Chat de visitante (tela de login): mostra a triagem em vez do usuário-sombra. */
  isGuest?: boolean
  /** Conta já vinculada ao visitante (quando a lista já sabe). */
  guestLinkedUserId?: number | null
  /** Avisa o pai que o visitante foi vinculado a uma conta real. */
  onGuestLinked?: (userId: number) => void
  /** Renderizado dentro de outro painel (conta candidata): sem largura fixa, sem header, sem Drawer. */
  embedded?: boolean
}) {
  const navigate = useNavigate()
  const { canManageSystem } = useAuth()
  const isMobileBp = useBreakpointValue({ base: true, lg: false })
  const isMobile = embedded ? false : isMobileBp
  const [linkedUserId, setLinkedUserId] = useState<number | null>(guestLinkedUserId)
  useEffect(() => {
    setLinkedUserId(guestLinkedUserId)
  }, [guestLinkedUserId, _chatId])
  const handleGuestLinked = useCallback(
    (linkedId: number) => {
      setLinkedUserId(linkedId)
      onGuestLinked?.(linkedId)
    },
    [onGuestLinked]
  )
  /** Visitante ainda sem conta vinculada: o userId é o usuário-sombra, não vale consultar. */
  const guestUnlinked = isGuest && !linkedUserId
  /** Depois do vínculo a lista pode ainda apontar para o sombra; a conta real prevalece. */
  const effectiveUserId = isGuest && linkedUserId ? linkedUserId : userId
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const [chatsCount, setChatsCount] = useState<ChatsCount | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [completedChats, setCompletedChats] = useState<CompletedChat[]>([])
  const [selectedChatMessages, setSelectedChatMessages] = useState<ChatMessage[]>([])
  const [selectedChatProtocol, setSelectedChatProtocol] = useState<string>('')
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const { isOpen: isHistoryModalOpen, onOpen: onOpenHistoryModal, onClose: onCloseHistoryModal } = useDisclosure()
  const [internalTickets, setInternalTickets] = useState<InternalTicketSummary[]>([])
  const [internalTicketsCount, setInternalTicketsCount] = useState(0)
  const [orderTickets, setOrderTickets] = useState<OrderTicketSummary[]>([])
  const [orderTicketsCount, setOrderTicketsCount] = useState(0)
  const [selectedTicketMessages, setSelectedTicketMessages] = useState<TicketMessage[]>([])
  const [selectedTicketTitle, setSelectedTicketTitle] = useState('')
  const [isLoadingTicketMessages, setIsLoadingTicketMessages] = useState(false)
  const { isOpen: isTicketModalOpen, onOpen: onOpenTicketModal, onClose: onCloseTicketModal } = useDisclosure()
  const [isCreateTicketOpen, setIsCreateTicketOpen] = useState(false)
  const [selectedCorporate, setSelectedCorporate] = useState<UserInfo['corporates']['list'][0] | null>(null)
  const { isOpen: isCorporateModalOpen, onOpen: onOpenCorporateModal, onClose: onCloseCorporateModal } = useDisclosure()

  const bgSurface = useColorModeValue('white', 'gray.800')
  const bgSubtle = useColorModeValue('gray.100', 'gray.700')
  const bgHover = useColorModeValue('gray.200', 'gray.600')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const iconMuted = useColorModeValue('gray.500', 'gray.400')

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const [infoRes, countRes] = await Promise.all([
          api.get(`/users/${effectiveUserId}/info`),
          api.get(`/users/${effectiveUserId}/chats-count`),
        ])
        setUserInfo(infoRes.data)
        setChatsCount(countRes.data)
      } catch (error) {
        console.error('Error fetching user info:', error)
      } finally {
        setIsLoading(false)
      }

      try {
        const res = await api.get(`/users/${effectiveUserId}/completed-chats-all`)
        setCompletedChats(res.data.data || [])
      } catch (error) {
        console.error('Error fetching completed chats:', error)
      }

      try {
        const res = await api.get(`/users/${effectiveUserId}/tickets`)
        setOrderTickets(res.data.orderTickets?.list || [])
        setOrderTicketsCount(res.data.orderTickets?.total || 0)
        setInternalTickets(res.data.internalTickets?.list || [])
        setInternalTicketsCount(res.data.internalTickets?.total || 0)
      } catch (error) {
        console.error('Error fetching user tickets:', error)
      }
    }

    if (guestUnlinked) {
      setIsLoading(false)
      return
    }
    if (effectiveUserId) {
      fetchData()
    } else {
      setIsLoading(false)
    }
  }, [effectiveUserId, _chatId, guestUnlinked])

  const handleViewChatMessages = async (chat: CompletedChat) => {
    setSelectedChatProtocol(chat.protocol || `#${chat.id}`)
    setIsLoadingMessages(true)
    onOpenHistoryModal()
    try {
      const res = await api.get(`/users/${effectiveUserId}/completed-chats/${chat.id}/messages`)
      setSelectedChatMessages(res.data.data || [])
    } catch (error) {
      console.error('Error fetching chat messages:', error)
      setSelectedChatMessages([])
    } finally {
      setIsLoadingMessages(false)
    }
  }

  const handleViewOrderTicket = async (ticket: OrderTicketSummary) => {
    setSelectedTicketTitle(`Ticket #${ticket.id}${ticket.orderId ? ` — Pedido #${ticket.orderId}` : ''}`)
    setIsLoadingTicketMessages(true)
    onOpenTicketModal()
    try {
      const res = await api.get(`/users/${effectiveUserId}/tickets/${ticket.id}/messages`)
      setSelectedTicketMessages(res.data.data || [])
    } catch (error) {
      console.error('Error fetching order ticket messages:', error)
      setSelectedTicketMessages([])
    } finally {
      setIsLoadingTicketMessages(false)
    }
  }

  const handleViewInternalTicket = async (ticket: InternalTicketSummary) => {
    setSelectedTicketTitle(`Ticket Interno #${ticket.id} — ${ticket.subject}`)
    setIsLoadingTicketMessages(true)
    onOpenTicketModal()
    try {
      const res = await api.get(`/users/${effectiveUserId}/internal-tickets/${ticket.id}/messages`)
      setSelectedTicketMessages(res.data.data || [])
    } catch (error) {
      console.error('Error fetching internal ticket messages:', error)
      setSelectedTicketMessages([])
    } finally {
      setIsLoadingTicketMessages(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  const formatDateSafe = (dateString: string | null | undefined, formatStr: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return '-'
      return format(date, formatStr, { locale: ptBR })
    } catch {
      return '-'
    }
  }

  const outerBoxProps = embedded
    ? { w: '100%' }
    : {
        w: { base: '100%', lg: '320px' },
        minW: { base: '100%', lg: '280px', xl: '320px' },
        maxW: { base: '100%', lg: '400px' },
        borderLeft: { base: 'none', lg: '1px' },
        borderColor,
        overflowY: 'auto' as const,
        h: '100%',
      }

  if (guestUnlinked) {
    const guestContent = (
      <Box {...outerBoxProps} bg={bgSurface}>
        <HStack p={{ base: 3, md: 4 }} borderBottom="1px" borderColor={borderColor} justify="space-between">
          <Text fontWeight="semibold" color={textPrimary} fontSize={{ base: 'sm', md: 'md' }}>
            Dados informados no login
          </Text>
          <IconButton
            aria-label="Fechar"
            icon={<X size={18} />}
            variant="ghost"
            size="sm"
            onClick={onClose}
            display={{ base: 'flex', lg: 'none' }}
          />
        </HStack>
        <GuestInfoPanel
          chatId={_chatId}
          onLinked={handleGuestLinked}
          renderUserPanel={(candidateId) => (
            <UserInfoSidebar chatId={_chatId} userId={candidateId} onClose={onClose} embedded />
          )}
        />
      </Box>
    )
    if (isMobile) {
      return (
        <Drawer isOpen={isOpen ?? false} placement="right" onClose={onClose} size="sm">
          <DrawerOverlay />
          <DrawerContent bg={bgSurface}>
            <DrawerCloseButton />
            <DrawerBody p={0}>{guestContent}</DrawerBody>
          </DrawerContent>
        </Drawer>
      )
    }
    return guestContent
  }

  if (isLoading) {
    const loadingContent = (
      <Box w={embedded ? '100%' : { base: '100%', lg: '320px' }} bg={bgSurface} p={4}>
        <Spinner size="lg" color="yellow.500" />
      </Box>
    )
    
    if (isMobile) {
      return (
        <Drawer isOpen={isOpen ?? false} placement="right" onClose={onClose} size="sm">
          <DrawerOverlay />
          <DrawerContent bg={bgSurface}>
            <DrawerCloseButton />
            <DrawerBody p={0}>{loadingContent}</DrawerBody>
          </DrawerContent>
        </Drawer>
      )
    }
    return loadingContent
  }

  if (!userInfo) {
    const emptyContent = (
      <Box w={embedded ? '100%' : { base: '100%', lg: '320px' }} bg={bgSurface} p={4}>
        <Text color={textMuted}>Informações não disponíveis</Text>
      </Box>
    )
    
    if (isMobile) {
      return (
        <Drawer isOpen={isOpen ?? false} placement="right" onClose={onClose} size="sm">
          <DrawerOverlay />
          <DrawerContent bg={bgSurface}>
            <DrawerCloseButton />
            <DrawerBody p={0}>{emptyContent}</DrawerBody>
          </DrawerContent>
        </Drawer>
      )
    }
    return emptyContent
  }

  const sidebarContent = (
    <Box {...outerBoxProps} bg={bgSurface}>
      {/* Header */}
      {!embedded && (
        <HStack p={{ base: 3, md: 4 }} borderBottom="1px" borderColor={borderColor} justify="space-between">
          <Text fontWeight="semibold" color={textPrimary} fontSize={{ base: 'sm', md: 'md' }}>
            Informações do Usuário
          </Text>
          <IconButton
            aria-label="Fechar"
            icon={<X size={18} />}
            variant="ghost"
            size="sm"
            onClick={onClose}
            display={{ base: 'flex', lg: 'none' }}
          />
        </HStack>
      )}

      {/* Visitante já vinculado: resumo da triagem em cima, conta real embaixo */}
      {isGuest && linkedUserId && !embedded && (
        <Box borderBottom="1px" borderColor={borderColor}>
          <GuestInfoPanel chatId={_chatId} compact onLinked={handleGuestLinked} />
        </Box>
      )}

      <VStack spacing={4} p={{ base: 3, md: 4 }} align="stretch">
        {/* User profile */}
        <VStack spacing={3}>
          <Avatar
            size={{ base: 'lg', md: 'xl' }}
            name={userInfo.user.name}
            src={userInfo.user.profilePic || undefined}
            bg="yellow.500"
            color="gray.900"
          />
          <VStack spacing={1}>
            <Text fontWeight="bold" color={textPrimary} textAlign="center" fontSize={{ base: 'sm', md: 'md' }} noOfLines={2}>
              {userInfo.user.name}
            </Text>
            <HStack spacing={1} justify="center" flexWrap="wrap" align="center">
              <Box 
                as="span" 
                color={iconMuted}
                display="inline-flex"
                flexShrink={0}
                lineHeight={1}
              >
                <EnvelopeSimple size={14} />
              </Box>
              <Text fontSize={{ base: 'xs', md: 'sm' }} color={textMuted} noOfLines={1}>
                {userInfo.user.email}
              </Text>
            </HStack>
            {userInfo.user.phone && (
              <HStack spacing={1} justify="center" flexWrap="wrap" align="center">
                <Box 
                  as="span" 
                  color={iconMuted}
                  display="inline-flex"
                  flexShrink={0}
                  lineHeight={1}
                >
                  <Phone size={14} />
                </Box>
                <Text fontSize={{ base: 'xs', md: 'sm' }} color={textMuted} noOfLines={1}>
                  {userInfo.user.phone}
                </Text>
              </HStack>
            )}
            <HStack spacing={1} justify="center" flexWrap="wrap" align="center">
              <Box 
                as="span" 
                color={iconMuted}
                display="inline-flex"
                flexShrink={0}
                lineHeight={1}
              >
                <Calendar size={14} />
              </Box>
              <Text fontSize={{ base: 'xs', md: 'sm' }} color={textMuted} textAlign="center">
                Cliente desde {formatDateSafe(userInfo.user.createdAt, "dd/MM/yyyy")}
              </Text>
            </HStack>
          </VStack>

          {/* Planos e acessos — catálogo completo do admin */}
          <AccessTags
            tags={userInfo.accessTags}
            size="md"
            withIcons
            showDefault
            justify="center"
          />
          {userInfo.webotIntegrationsLimit != null && (
            <Text fontSize="xs" color={textMuted} textAlign="center">
              AutoPilot: {userInfo.webotIntegrationsLimit}{' '}
              {userInfo.webotIntegrationsLimit === 1 ? 'integração' : 'integrações'}
            </Text>
          )}
        </VStack>

        <Divider borderColor={borderColor} />

        {/* Stats */}
        <StatGroup>
          <Stat>
            <StatLabel color={textMuted} fontSize="xs">
              Empresas
            </StatLabel>
            <StatNumber color="blue.400" fontSize="lg">
              {userInfo.corporates.total}
            </StatNumber>
          </Stat>
          <Stat>
            <StatLabel color={textMuted} fontSize="xs">
              Integrações
            </StatLabel>
            <StatNumber color="purple.400" fontSize="lg">
              {userInfo.integrations.total}
            </StatNumber>
          </Stat>
          <Stat>
            <StatLabel color={textMuted} fontSize="xs">
              Pedidos
            </StatLabel>
            <StatNumber color="orange.400" fontSize="lg">
              {userInfo.orders.total}
            </StatNumber>
          </Stat>
        </StatGroup>

        <Stat>
          <StatLabel color={textMuted} fontSize="xs">
            Saldo em Carteira
          </StatLabel>
          <StatNumber color="green.400" fontSize="lg">
            {formatCurrency(userInfo.wallet.totalBalance)}
          </StatNumber>
        </Stat>

        {/* Chats count */}
        {chatsCount && (
          <>
            <Divider borderColor={borderColor} />
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color={textSecondary} mb={2}>
                Atendimentos deste usuário
              </Text>
              <HStack justify="space-between">
                <VStack spacing={0}>
                  <Text fontSize="lg" fontWeight="bold" color="yellow.400">
                    {chatsCount.pending}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Pendentes
                  </Text>
                </VStack>
                <VStack spacing={0}>
                  <Text fontSize="lg" fontWeight="bold" color="blue.400">
                    {chatsCount.inProgress}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Em andamento
                  </Text>
                </VStack>
                <VStack spacing={0}>
                  <Text fontSize="lg" fontWeight="bold" color="green.400">
                    {chatsCount.completed}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Concluídos
                  </Text>
                </VStack>
              </HStack>
            </Box>
          </>
        )}

        <Divider borderColor={borderColor} />

        {/* Criar Ticket Interno */}
        <Button
          size="sm"
          w="100%"
          variant="outline"
          colorScheme="yellow"
          leftIcon={<Plus size={14} weight="bold" />}
          rightIcon={<Ticket size={14} />}
          onClick={() => setIsCreateTicketOpen(true)}
        >
          Criar Ticket Interno
        </Button>

        {/* Accordion sections */}
        <Accordion allowMultiple>
          {/* Corporates */}
          {userInfo.corporates.total > 0 && (
            <AccordionItem border="none">
              <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                <HStack flex={1}>
                  <Buildings size={18} color="#ECC94B" />
                  <Text fontSize="sm" fontWeight="medium" color={textPrimary}>
                    Empresas ({userInfo.corporates.total})
                  </Text>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px={0}>
                <VStack spacing={2} align="stretch">
                  {userInfo.corporates.list.map((corp) => (
                    <Box
                      key={corp.id}
                      p={2}
                      bg={bgSubtle}
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: bgHover }}
                      transition="background 0.2s"
                      onClick={() => {
                        setSelectedCorporate(corp)
                        onOpenCorporateModal()
                      }}
                    >
                      <HStack justify="space-between" align="start">
                        <VStack align="start" spacing={0} flex={1}>
                          <Text fontSize="sm" fontWeight="medium" noOfLines={1} color={textPrimary}>
                            {corp.corporateName}
                          </Text>
                          <Text fontSize="xs" color={textMuted} mt={1}>
                            {corp.employerNumber}
                          </Text>
                        </VStack>
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          color={corp.walletBalance > 0 ? 'green.400' : 'gray.500'}
                          flexShrink={0}
                          ml={2}
                        >
                          {formatCurrency(corp.walletBalance)}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          )}

          {/* Integrations */}
          {userInfo.integrations.total > 0 && (
            <AccordionItem border="none">
              <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                <HStack flex={1}>
                  <Plug size={18} color="#ECC94B" />
                  <Text fontSize="sm" fontWeight="medium" color={textPrimary}>
                    Integrações ({userInfo.integrations.total})
                  </Text>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px={0}>
                <VStack spacing={2} align="stretch">
                  {userInfo.integrations.list.map((int) => (
                    <Box
                      key={int.id}
                      p={2}
                      bg={bgSubtle}
                      borderRadius="md"
                    >
                      <Text fontSize="sm" fontWeight="medium" noOfLines={1} color={textPrimary}>
                        {int.name}
                      </Text>
                      <HStack justify="space-between" mt={1}>
                        <Badge colorScheme="blue" fontSize="xs">
                          {int.channel.name}
                        </Badge>
                        <Badge
                          colorScheme={int.itemsCount > 0 ? 'green' : 'gray'}
                          fontSize="xs"
                        >
                          {int.itemsCount} anúncios
                        </Badge>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          )}

          {/* Order Tickets */}
          {orderTicketsCount > 0 && (
            <AccordionItem border="none">
              <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                <HStack flex={1}>
                  <ShoppingBag size={18} color="#ECC94B" />
                  <Text fontSize="sm" fontWeight="medium" color={textPrimary}>
                    Tickets de Pedidos ({orderTicketsCount})
                  </Text>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px={0}>
                <VStack spacing={2} align="stretch">
                  {orderTickets.map((ticket) => (
                    <Box
                      key={ticket.id}
                      p={3}
                      bg={bgSubtle}
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: bgHover }}
                      transition="background 0.2s"
                      onClick={() => handleViewOrderTicket(ticket)}
                    >
                      <HStack justify="space-between" mb={1}>
                        <Text fontSize="xs" fontWeight="bold" color="yellow.400">
                          Ticket #{ticket.id}
                        </Text>
                        <IconButton
                          aria-label="Ver mensagens"
                          icon={<Eye size={14} />}
                          variant="ghost"
                          size="xs"
                          colorScheme="yellow"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleViewOrderTicket(ticket)
                          }}
                        />
                      </HStack>
                      {ticket.orderId && (
                        <Text fontSize="xs" color={textMuted} noOfLines={1}>
                          Pedido #{ticket.orderId}
                          {ticket.channelOrderNumber ? ` (${ticket.channelOrderNumber})` : ''}
                        </Text>
                      )}
                      <HStack spacing={1} mt={1} flexWrap="wrap">
                        {ticket.categoryName && (
                          <Badge fontSize="2xs" colorScheme="blue">
                            {ticket.categoryName}
                          </Badge>
                        )}
                        {ticket.customStatusName ? (
                          <Badge
                            fontSize="2xs"
                            bg={ticket.customStatusColor || undefined}
                            colorScheme={ticket.customStatusColor ? undefined : 'purple'}
                            color={ticket.customStatusColor ? 'white' : undefined}
                          >
                            {ticket.customStatusName}
                          </Badge>
                        ) : (
                          <Badge
                            fontSize="2xs"
                            colorScheme={orderTicketStatusColors[ticket.status] || 'gray'}
                          >
                            {orderTicketStatusLabels[ticket.status] || ticket.status}
                          </Badge>
                        )}
                      </HStack>
                      <HStack justify="flex-end" mt={1}>
                        <Text fontSize="xs" color="gray.500">
                          {formatDateSafe(ticket.createdAt, 'dd/MM/yy')}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          )}

          {/* Internal Tickets */}
          {internalTicketsCount > 0 && (
            <AccordionItem border="none">
              <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                <HStack flex={1}>
                  <Ticket size={18} color="#ECC94B" />
                  <Text fontSize="sm" fontWeight="medium" color={textPrimary}>
                    Tickets Internos ({internalTicketsCount})
                  </Text>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px={0}>
                <VStack spacing={2} align="stretch">
                  {internalTickets.map((ticket) => (
                    <Box
                      key={ticket.id}
                      p={3}
                      bg={bgSubtle}
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: bgHover }}
                      transition="background 0.2s"
                      onClick={() => handleViewInternalTicket(ticket)}
                      borderLeft="3px solid"
                      borderLeftColor={`${ticketPriorityColors[ticket.priority] || 'gray'}.400`}
                    >
                      <HStack justify="space-between" align="start">
                        <Text fontSize="sm" fontWeight="medium" noOfLines={1} color={textPrimary} flex={1}>
                          {ticket.subject}
                        </Text>
                        <IconButton
                          aria-label="Abrir ticket"
                          icon={<Eye size={14} />}
                          variant="ghost"
                          size="xs"
                          colorScheme="yellow"
                          flexShrink={0}
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(canManageSystem ? `/internal-tickets/${ticket.id}` : `/my-internal-tickets/${ticket.id}`)
                          }}
                        />
                      </HStack>
                      <HStack spacing={1} mt={1}>
                        <Badge
                          fontSize="2xs"
                          colorScheme={ticketPriorityColors[ticket.priority] || 'gray'}
                        >
                          {ticketPriorityLabels[ticket.priority] || ticket.priority}
                        </Badge>
                        <Badge
                          fontSize="2xs"
                          colorScheme={ticketStatusColors[ticket.status] || 'gray'}
                        >
                          {ticketStatusLabels[ticket.status] || ticket.status}
                        </Badge>
                      </HStack>
                      <HStack justify="space-between" mt={1}>
                        <Text fontSize="xs" color={textMuted} noOfLines={1}>
                          {ticket.creatorName} → {ticket.assignedName}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {formatDateSafe(ticket.createdAt, 'dd/MM/yy')}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          )}

          {/* Completed chats history */}
          {completedChats.length > 0 && (
            <AccordionItem border="none">
              <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                <HStack flex={1}>
                  <ClockCounterClockwise size={18} color="#ECC94B" />
                  <Text fontSize="sm" fontWeight="medium" color={textPrimary}>
                    Atendimentos ({completedChats.length})
                  </Text>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px={0}>
                <VStack spacing={2} align="stretch">
                  {completedChats.map((chat) => (
                    <Box
                      key={chat.id}
                      p={3}
                      bg={bgSubtle}
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: bgHover }}
                      transition="background 0.2s"
                      onClick={() => handleViewChatMessages(chat)}
                    >
                      <HStack justify="space-between" mb={1}>
                        <Text fontSize="xs" fontWeight="bold" color="yellow.400">
                          {chat.protocol || `#${chat.id}`}
                        </Text>
                        <IconButton
                          aria-label="Ver mensagens"
                          icon={<Eye size={14} />}
                          variant="ghost"
                          size="xs"
                          colorScheme="yellow"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleViewChatMessages(chat)
                          }}
                        />
                      </HStack>
                      {chat.categoryName && (
                        <Badge
                          fontSize="2xs"
                          colorScheme="blue"
                          mb={1}
                          bg={chat.categoryColor || undefined}
                          color={chat.categoryColor ? textPrimary : undefined}
                        >
                          {chat.categoryName}
                        </Badge>
                      )}
                      {chat.agentName && (
                        <HStack spacing={1} mb={1} align="center">
                          <Box 
                            as="span" 
                            color={iconMuted}
                            display="inline-flex"
                            flexShrink={0}
                            lineHeight={1}
                          >
                            <UserCircle size={12} />
                          </Box>
                          <Text fontSize="xs" color={textMuted}>
                            Atendido por: {chat.agentName}
                          </Text>
                        </HStack>
                      )}
                      <HStack justify="space-between" mt={1} align="center">
                        <HStack spacing={1} align="center">
                          <Box 
                            as="span" 
                            color={iconMuted}
                            display="inline-flex"
                            flexShrink={0}
                            lineHeight={1}
                          >
                            <ChatDots size={12} />
                          </Box>
                          <Text fontSize="xs" color={textMuted}>
                            {chat.messageCount} msgs
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                          {formatDateSafe(chat.resolvedAt || chat.updatedAt, "dd/MM/yy HH:mm")}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          )}
        </Accordion>
      </VStack>

      {/* Modal de histórico de mensagens */}
      <Modal isOpen={isHistoryModalOpen} onClose={onCloseHistoryModal} size="xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg={bgSurface} maxH="80vh">
          <ModalHeader borderBottom="1px" borderColor={borderColor}>
            <HStack>
              <ClockCounterClockwise size={20} color="#ECC94B" />
              <Text color={textPrimary} fontSize="md">
                Histórico — {selectedChatProtocol}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={textMuted} />
          <ModalBody py={4}>
            {isLoadingMessages ? (
              <Flex justify="center" align="center" minH="200px">
                <Spinner size="lg" color="yellow.500" />
              </Flex>
            ) : selectedChatMessages.length === 0 ? (
              <Flex justify="center" align="center" minH="200px">
                <Text color="gray.500">Nenhuma mensagem encontrada</Text>
              </Flex>
            ) : (
              <VStack spacing={3} align="stretch">
                {selectedChatMessages.map((msg) => {
                  const isAgent = msg.senderType === 'agent'
                  const isSystem = msg.senderType === 'system'

                  if (isSystem) {
                    return (
                      <Flex key={msg.id} justify="center">
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          bg={bgSubtle}
                          px={3}
                          py={1}
                          borderRadius="full"
                          textAlign="center"
                          textTransform="uppercase"
                          fontWeight="semibold"
                        >
                          {msg.message}
                        </Text>
                      </Flex>
                    )
                  }

                  return (
                    <Flex
                      key={msg.id}
                      justify={isAgent ? 'flex-end' : 'flex-start'}
                    >
                      <Box
                        maxW="80%"
                        bg={isAgent ? 'yellow.600' : bgSubtle}
                        color={isAgent ? 'white' : textPrimary}
                        px={3}
                        py={2}
                        borderRadius="lg"
                        borderTopRightRadius={isAgent ? '2px' : 'lg'}
                        borderTopLeftRadius={isAgent ? 'lg' : '2px'}
                      >
                        <Text fontSize="xs" fontWeight="bold" mb={1} color={isAgent ? 'yellow.100' : 'blue.300'}>
                          {msg.senderName || (isAgent ? 'Atendente' : 'Usuário')}
                        </Text>
                        {msg.messageType === 'image' && msg.fileUrl ? (
                          <Image
                            src={msg.fileUrl}
                            alt={msg.fileName || 'Imagem'}
                            maxH="200px"
                            borderRadius="md"
                            mb={1}
                          />
                        ) : msg.messageType === 'file' && msg.fileUrl ? (
                          <HStack
                            bg={isAgent ? 'yellow.700' : bgHover}
                            p={2}
                            borderRadius="md"
                            mb={1}
                            as="a"
                            href={msg.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Download size={16} />
                            <Text fontSize="xs" noOfLines={1}>
                              {msg.fileName || 'Arquivo'}
                            </Text>
                          </HStack>
                        ) : null}
                        {msg.message && (
                          <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
                            {msg.message}
                          </Text>
                        )}
                        <Text fontSize="2xs" color={isAgent ? 'yellow.200' : 'gray.500'} textAlign="right" mt={1}>
                          {formatDateSafe(msg.createdAt, "dd/MM/yy HH:mm")}
                        </Text>
                      </Box>
                    </Flex>
                  )
                })}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal de visualização de tickets (pedidos e internos) */}
      <Modal isOpen={isTicketModalOpen} onClose={onCloseTicketModal} size="xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg={bgSurface} maxH="80vh">
          <ModalHeader borderBottom="1px" borderColor={borderColor}>
            <HStack>
              <Ticket size={20} color="#ECC94B" />
              <Text color={textPrimary} fontSize="md" noOfLines={1}>
                {selectedTicketTitle}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={textMuted} />
          <ModalBody py={4}>
            {isLoadingTicketMessages ? (
              <Flex justify="center" align="center" minH="200px">
                <Spinner size="lg" color="yellow.500" />
              </Flex>
            ) : selectedTicketMessages.length === 0 ? (
              <Flex justify="center" align="center" minH="200px">
                <Text color="gray.500">Nenhuma mensagem encontrada</Text>
              </Flex>
            ) : (
              <VStack spacing={3} align="stretch">
                {selectedTicketMessages.map((msg) => {
                  const isAgent = msg.senderType === 'agent'
                  const isSystem = msg.senderType === 'system'

                  if (isSystem || (msg.statusChange && !msg.message && !msg.fileUrl)) {
                    return (
                      <Flex key={msg.id} justify="center">
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          bg={bgSubtle}
                          px={3}
                          py={1}
                          borderRadius="full"
                          textAlign="center"
                          textTransform="uppercase"
                          fontWeight="semibold"
                        >
                          {msg.message || `Status alterado: ${msg.statusChange}`}
                        </Text>
                      </Flex>
                    )
                  }

                  return (
                    <Flex
                      key={msg.id}
                      justify={isAgent ? 'flex-end' : 'flex-start'}
                    >
                      <Box
                        maxW="80%"
                        bg={isAgent ? 'yellow.600' : bgSubtle}
                        color={isAgent ? 'white' : textPrimary}
                        px={3}
                        py={2}
                        borderRadius="lg"
                        borderTopRightRadius={isAgent ? '2px' : 'lg'}
                        borderTopLeftRadius={isAgent ? 'lg' : '2px'}
                      >
                        <Text fontSize="xs" fontWeight="bold" mb={1} color={isAgent ? 'yellow.100' : 'blue.300'}>
                          {msg.senderName || (isAgent ? 'Atendente' : 'Usuário')}
                        </Text>
                        {msg.statusChange && (
                          <Badge fontSize="2xs" colorScheme="purple" mb={1}>
                            Status: {msg.statusChange}
                          </Badge>
                        )}
                        {msg.messageType === 'image' && msg.fileUrl ? (
                          <Image
                            src={msg.fileUrl}
                            alt={msg.fileName || 'Imagem'}
                            maxH="200px"
                            borderRadius="md"
                            mb={1}
                          />
                        ) : msg.fileUrl ? (
                          <HStack
                            bg={isAgent ? 'yellow.700' : bgHover}
                            p={2}
                            borderRadius="md"
                            mb={1}
                            as="a"
                            href={msg.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Download size={16} />
                            <Text fontSize="xs" noOfLines={1}>
                              {msg.fileName || 'Arquivo'}
                            </Text>
                          </HStack>
                        ) : null}
                        {(msg.message || msg.caption) && (
                          <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
                            {msg.message || msg.caption}
                          </Text>
                        )}
                        <Text fontSize="2xs" color={isAgent ? 'yellow.200' : 'gray.500'} textAlign="right" mt={1}>
                          {formatDateSafe(msg.createdAt, "dd/MM/yy HH:mm")}
                        </Text>
                      </Box>
                    </Flex>
                  )
                })}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Create Internal Ticket Modal */}
      <CreateTicketModal
        isOpen={isCreateTicketOpen}
        onClose={() => setIsCreateTicketOpen(false)}
        onCreated={() => {
          setIsCreateTicketOpen(false)
          api.get(`/users/${effectiveUserId}/tickets`)
            .then((res) => {
              setInternalTickets(res.data.internalTickets?.list || [])
              setInternalTicketsCount(res.data.internalTickets?.total || 0)
            })
            .catch((err) => console.error('Error refreshing internal tickets:', err))
        }}
        prefilledChatId={_chatId}
      />

      {/* Corporate Details Modal */}
      <Modal isOpen={isCorporateModalOpen} onClose={onCloseCorporateModal} size="lg" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg={bgSurface} maxH="80vh">
          <ModalHeader borderBottom="1px" borderColor={borderColor}>
            <HStack>
              <Buildings size={20} color="#ECC94B" />
              <Text color={textPrimary} fontSize="md">
                Detalhes da Empresa
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={textMuted} />
          <ModalBody py={4}>
            {selectedCorporate ? (
              <VStack spacing={4} align="stretch">
                {/* Informações Básicas */}
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color={textMuted} mb={2} textTransform="uppercase">
                    Informações Básicas
                  </Text>
                  <VStack spacing={2} align="stretch" pl={2}>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={textSecondary}>Nome:</Text>
                      <Text fontSize="sm" fontWeight="medium" color={textPrimary}>{selectedCorporate.corporateName}</Text>
                    </HStack>
                    {selectedCorporate.razaoSocial && (
                      <HStack justify="space-between">
                        <Text fontSize="sm" color={textSecondary}>Razão Social:</Text>
                        <Text fontSize="sm" fontWeight="medium" color={textPrimary}>{selectedCorporate.razaoSocial}</Text>
                      </HStack>
                    )}
                    {selectedCorporate.nomeFantasia && (
                      <HStack justify="space-between">
                        <Text fontSize="sm" color={textSecondary}>Nome Fantasia:</Text>
                        <Text fontSize="sm" fontWeight="medium" color={textPrimary}>{selectedCorporate.nomeFantasia}</Text>
                      </HStack>
                    )}
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={textSecondary}>
                        {selectedCorporate.mode === 'J' ? 'CNPJ:' : 'CPF:'}
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" color={textPrimary}>{selectedCorporate.employerNumber}</Text>
                    </HStack>
                    {selectedCorporate.ie && (
                      <HStack justify="space-between">
                        <Text fontSize="sm" color={textSecondary}>IE:</Text>
                        <Text fontSize="sm" fontWeight="medium" color={textPrimary}>{selectedCorporate.ie}</Text>
                      </HStack>
                    )}
                    {selectedCorporate.ieSt && (
                      <HStack justify="space-between">
                        <Text fontSize="sm" color={textSecondary}>IE ST:</Text>
                        <Text fontSize="sm" fontWeight="medium" color={textPrimary}>{selectedCorporate.ieSt}</Text>
                      </HStack>
                    )}
                    {selectedCorporate.stateRegistration && (
                      <HStack justify="space-between">
                        <Text fontSize="sm" color={textSecondary}>Inscrição Estadual:</Text>
                        <Text fontSize="sm" fontWeight="medium" color={textPrimary}>{selectedCorporate.stateRegistration}</Text>
                      </HStack>
                    )}
                    {selectedCorporate.cityRegistration && (
                      <HStack justify="space-between">
                        <Text fontSize="sm" color={textSecondary}>Inscrição Municipal:</Text>
                        <Text fontSize="sm" fontWeight="medium" color={textPrimary}>{selectedCorporate.cityRegistration}</Text>
                      </HStack>
                    )}
                    {selectedCorporate.im && (
                      <HStack justify="space-between">
                        <Text fontSize="sm" color={textSecondary}>IM:</Text>
                        <Text fontSize="sm" fontWeight="medium" color={textPrimary}>{selectedCorporate.im}</Text>
                      </HStack>
                    )}
                  </VStack>
                </Box>

                <Divider borderColor={borderColor} />

                {/* Endereço */}
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color={textMuted} mb={2} textTransform="uppercase">
                    Endereço
                  </Text>
                  <VStack spacing={2} align="stretch" pl={2}>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={textSecondary}>Logradouro:</Text>
                      <Text fontSize="sm" fontWeight="medium" color={textPrimary} textAlign="right">
                        {selectedCorporate.address}
                        {selectedCorporate.number && `, ${selectedCorporate.number}`}
                        {selectedCorporate.complement && ` - ${selectedCorporate.complement}`}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={textSecondary}>Bairro:</Text>
                      <Text fontSize="sm" fontWeight="medium" color={textPrimary}>{selectedCorporate.neighborhood}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={textSecondary}>Cidade/UF:</Text>
                      <Text fontSize="sm" fontWeight="medium" color={textPrimary}>
                        {selectedCorporate.city} / {selectedCorporate.state}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={textSecondary}>CEP:</Text>
                      <Text fontSize="sm" fontWeight="medium" color={textPrimary}>{selectedCorporate.zipCode}</Text>
                    </HStack>
                  </VStack>
                </Box>

                <Divider borderColor={borderColor} />

                {/* Informações Adicionais */}
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color={textMuted} mb={2} textTransform="uppercase">
                    Informações Adicionais
                  </Text>
                  <VStack spacing={2} align="stretch" pl={2}>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={textSecondary}>Status:</Text>
                      <Badge
                        colorScheme={
                          selectedCorporate.status === 'active' ? 'green' :
                          selectedCorporate.status === 'pending' ? 'yellow' :
                          selectedCorporate.status === 'rejected' ? 'red' : 'gray'
                        }
                        fontSize="xs"
                      >
                        {selectedCorporate.status === 'active' ? 'Ativa' :
                         selectedCorporate.status === 'pending' ? 'Pendente' :
                         selectedCorporate.status === 'rejected' ? 'Rejeitada' : selectedCorporate.status}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={textSecondary}>Saldo em Carteira:</Text>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color={selectedCorporate.walletBalance > 0 ? 'green.400' : 'gray.500'}
                      >
                        {formatCurrency(selectedCorporate.walletBalance)}
                      </Text>
                    </HStack>
                    {selectedCorporate.taxRegime !== null && (
                      <HStack justify="space-between">
                        <Text fontSize="sm" color={textSecondary}>Regime Tributário:</Text>
                        <Text fontSize="sm" fontWeight="medium" color={textPrimary}>
                          {selectedCorporate.taxRegime === 1 ? 'Simples Nacional' :
                           selectedCorporate.taxRegime === 2 ? 'Lucro Presumido' :
                           selectedCorporate.taxRegime === 3 ? 'Lucro Real' : selectedCorporate.taxRegime}
                        </Text>
                      </HStack>
                    )}
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={textSecondary}>Criada em:</Text>
                      <Text fontSize="sm" fontWeight="medium" color={textPrimary}>
                        {formatDateSafe(selectedCorporate.createdAt, "dd/MM/yyyy")}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            ) : (
              <Text color={textMuted}>Carregando informações...</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )

  if (isMobile) {
    return (
      <Drawer isOpen={isOpen ?? false} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent bg={bgSurface}>
          <DrawerCloseButton />
          <DrawerBody p={0}>{sidebarContent}</DrawerBody>
        </DrawerContent>
      </Drawer>
    )
  }

  return sidebarContent
}
